"use client";

import * as React from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type HTMLMotionProps,
} from "framer-motion";
import { Collapsible as CollapsiblePrimitive } from "@base-ui-components/react/collapsible";

import { Icon, type IconName } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { KbdShortcut } from "@/components/ui/kbd-shortcut";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatEntityForms } from "@/lib/search/entity-variants";
import { cn } from "@/lib/utils";

const DEFAULT_EXPANDED_WIDTH = 371;
const DEFAULT_COLLAPSED_WIDTH = 48;

interface FiltersContextValue {
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
}

const FiltersContext = React.createContext<FiltersContextValue | null>(null);

function useFiltersContext(component: string) {
  const ctx = React.useContext(FiltersContext);
  if (!ctx) {
    throw new Error(
      `<${component}> must be rendered inside <Filters.Container>`,
    );
  }
  return ctx;
}

export type FiltersContainerProps = {
  collapsed: boolean;
  onCollapsedChange: (next: boolean) => void;
  expandedWidth?: number;
  collapsedWidth?: number;
  collapsedTopIcon?: IconName;
  collapsedCount?: number;
  renderChildrenOnly?: boolean;
  dataSlot?: string;
} & Omit<React.ComponentProps<"div">, "ref">;

function Container({
  collapsed,
  onCollapsedChange,
  expandedWidth = DEFAULT_EXPANDED_WIDTH,
  collapsedWidth = DEFAULT_COLLAPSED_WIDTH,
  collapsedTopIcon = "IconFilter2",
  collapsedCount,
  renderChildrenOnly = false,
  className,
  dataSlot = "filters-container",
  children,
  ...rest
}: FiltersContainerProps) {
  const ctxValue = React.useMemo<FiltersContextValue>(
    () => ({ collapsed, setCollapsed: onCollapsedChange }),
    [collapsed, onCollapsedChange],
  );

  if (renderChildrenOnly) {
    return (
      <FiltersContext.Provider value={ctxValue}>
        {children}
      </FiltersContext.Provider>
    );
  }

  return (
    <FiltersContext.Provider value={ctxValue}>
      <motion.div
        initial={false}
        animate={{ width: collapsed ? collapsedWidth : expandedWidth }}
        transition={{
          type: "spring" as const,
          damping: 30,
          stiffness: 500,
          mass: 0.5,
        }}
        className={cn(
          "flex h-full shrink-0 flex-col overflow-hidden border-r border-border bg-surface-primary",
          className,
        )}
        data-slot={dataSlot}
        {...(rest as HTMLMotionProps<"div">)}
      >
        <AnimatePresence mode="wait" initial={false}>
          {collapsed ? (
            <motion.button
              key="collapsed"
              type="button"
              onClick={() => onCollapsedChange(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
              className="group flex h-full w-12 cursor-pointer flex-col items-center pt-4 transition-colors hover:bg-contrast/4"
              aria-label="Expand filters"
            >
              <div className="relative flex size-[18px] items-center justify-center opacity-50 transition-opacity group-hover:opacity-90">
                <Icon
                  name={collapsedTopIcon}
                  size={18}
                  className="text-heading transition-opacity duration-150 group-hover:opacity-0"
                />
                <Icon
                  name="IconChevronDoubleRight"
                  size={18}
                  className="absolute inset-0 m-auto text-heading opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                />
              </div>
              {collapsedCount && collapsedCount > 0 ? (
                <div className="mt-4 flex h-5 min-w-5 items-center justify-center rounded border-[0.714px] border-[#ffaf53]/25 bg-[#ffaf53]/15 px-1 shadow-[0px_0.714px_1.429px_0px_rgba(0,0,0,0.05)]">
                  <span className="text-[11px] font-medium leading-[14.286px] tabular-nums text-[#ffaf53]">
                    {collapsedCount}
                  </span>
                </div>
              ) : null}
              <div className="mt-auto pb-4">
                <div className="flex size-7 items-center justify-center rounded-md opacity-50 transition-opacity group-hover:opacity-90">
                  <Icon
                    name="IconChevronDoubleRight"
                    size={18}
                    className="text-heading"
                  />
                </div>
              </div>
            </motion.button>
          ) : (
            <motion.div
              key="expanded"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
              className="flex h-full flex-col overflow-hidden"
            >
              <div className="flex h-full w-full flex-col overflow-hidden">
                {children}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </FiltersContext.Provider>
  );
}

export type FiltersHeaderProps = {
  actions?: React.ReactNode;
  /** Replaces the default "Filters" icon + label on the left (e.g. a pane
   * switcher). */
  leading?: React.ReactNode;
} & Omit<React.ComponentProps<"div">, "children">;

function Header({ className, actions, leading, ...rest }: FiltersHeaderProps) {
  const { setCollapsed } = useFiltersContext("Filters.Header");
  return (
    <div
      data-slot="filters-header"
      className={cn(
        "flex shrink-0 items-center justify-between gap-3 border-b border-border bg-contrast/1 px-4 h-11",
        className,
      )}
      {...rest}
    >
      {leading ?? (
        <div className="flex items-center gap-1.5">
          <div className="opacity-60">
            <Icon name="IconFilter2" size={18} className="text-heading" />
          </div>
          <span className="text-sm font-medium text-contrast/80">Filters</span>
        </div>
      )}
      <div className="flex shrink-0 items-center gap-2">
        {actions}
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="flex size-4 cursor-pointer items-center justify-center opacity-50 transition-opacity hover:opacity-80"
          aria-label="Collapse filters"
        >
          <Icon name="IconChevronDoubleLeft" size={16} className="text-heading" />
        </button>
      </div>
    </div>
  );
}

export type FiltersSubHeaderProps = React.ComponentProps<"div">;

function SubHeader({ className, children, ...rest }: FiltersSubHeaderProps) {
  return (
    <div
      data-slot="filters-sub-header"
      className={cn(
        "flex shrink-0 flex-col gap-2 border-b border-contrast/5 p-3",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export type FiltersBodyProps = React.ComponentProps<"div">;

function Body({ className, children, ...rest }: FiltersBodyProps) {
  const [scrolled, setScrolled] = React.useState(false);
  const viewportRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 0);
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      data-slot="filters-body"
      className={cn("relative min-h-0 flex-1", className)}
      {...rest}
    >
      <ScrollArea
        className="h-full"
        viewportRef={viewportRef}
        constrainContentWidth
      >
        {children}
      </ScrollArea>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-surface-primary/60 via-surface-primary/25 to-transparent transition-opacity duration-150",
          scrolled ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}

export type FiltersFooterProps = {
  variant?: "default" | "muted";
  height?: "default" | "fixed";
} & React.ComponentProps<"div">;

function Footer({
  className,
  variant = "default",
  height = "default",
  children,
  ...rest
}: FiltersFooterProps) {
  return (
    <div
      data-slot="filters-footer"
      className={cn(
        "shrink-0 border-t border-border",
        variant === "muted" && "bg-contrast/[0.02]",
        height === "fixed"
          ? "flex h-14 items-center px-4 lg:h-10"
          : "px-5 py-3",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

const SECTION_PANEL_HEIGHT_VAR = "--collapsible-panel-height" as const;
const SECTION_PANEL_WIDTH_VAR = "--collapsible-panel-width" as const;

const sectionPanelTransition = {
  height: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const },
  opacity: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const },
  pointerEvents: { duration: 0 },
} as const;

const sectionPanelTransitionReduced = {
  height: { duration: 0 },
  opacity: { duration: 0 },
  pointerEvents: { duration: 0 },
} as const;

function SectionMotionPanel({
  panelProps,
  state,
}: {
  panelProps: React.ComponentProps<"div"> & { ref?: React.Ref<HTMLDivElement> };
  state: CollapsiblePrimitive.Panel.State;
}) {
  const reduced = useReducedMotion();
  const { className, style, hidden, children, ref, ...rest } = panelProps;
  void hidden;

  const nextStyle: React.CSSProperties = { ...style };
  delete (nextStyle as Record<string, string | number | undefined>)[
    SECTION_PANEL_HEIGHT_VAR
  ];
  delete (nextStyle as Record<string, string | number | undefined>)[
    SECTION_PANEL_WIDTH_VAR
  ];

  return (
    <motion.div
      ref={ref as React.Ref<HTMLDivElement>}
      {...(rest as Omit<HTMLMotionProps<"div">, "ref" | "children">)}
      className={cn("max-w-full overflow-hidden", className)}
      style={{ ...nextStyle, overflow: "hidden" }}
      initial={false}
      animate={
        state.open
          ? { height: "auto", opacity: 1, pointerEvents: "auto" }
          : { height: 0, opacity: 0, pointerEvents: "none" }
      }
      transition={
        reduced ? sectionPanelTransitionReduced : sectionPanelTransition
      }
      aria-hidden={!state.open}
      inert={state.open ? undefined : true}
    >
      {children}
    </motion.div>
  );
}

export type FiltersPoolTab = {
  value: string;
  label: string;
  content?: React.ReactNode;
};

export type FiltersPoolTabsProps = {
  tabs: ReadonlyArray<FiltersPoolTab>;
  value: string;
  onValueChange: (next: string) => void;
  ariaLabel?: string;
  layoutId?: string;
} & Omit<React.ComponentProps<"div">, "onChange">;

function PoolTabs({
  tabs,
  value,
  onValueChange,
  ariaLabel,
  layoutId,
  className,
  ...rest
}: FiltersPoolTabsProps) {
  const generatedId = React.useId();
  const tabsLayoutId = layoutId ?? `filters-pool-tabs-${generatedId}`;
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      data-slot="filters-pool-tabs"
      className={cn(
        "flex w-full gap-1 rounded-md border border-border bg-surface-inset p-1",
        className,
      )}
      {...rest}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onValueChange(tab.value)}
            className={cn(
              "relative flex flex-1 cursor-pointer flex-col items-center gap-0.5 rounded-md px-3.5 py-1 transition-colors",
              !active && "hover:bg-contrast/[0.04]",
            )}
          >
            {active && (
              <motion.div
                layoutId={tabsLayoutId}
                className="absolute inset-0 rounded-md border border-border bg-surface-primary shadow-sm dark:border-white/10 dark:bg-surface-elevated dark:shadow-none"
                transition={{
                  type: "spring",
                  damping: 30,
                  stiffness: 500,
                  mass: 0.5,
                }}
              />
            )}
            <span
              className={cn(
                "relative z-10 text-[13px] font-medium transition-colors",
                active ? "text-heading" : "text-contrast/70",
              )}
            >
              {tab.label}
            </span>
            {tab.content !== undefined ? (
              <span
                className={cn(
                  "relative z-10 flex min-h-5 items-center justify-center text-sm font-medium tabular-nums transition-colors",
                  active ? "text-contrast/80" : "text-contrast/55",
                )}
              >
                {tab.content}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export type FiltersSectionProps = {
  icon: IconName;
  label: string;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  activeCount?: number;
  onClear?: () => void;
} & Omit<React.ComponentProps<"div">, "onChange">;

function Section({
  icon,
  label,
  defaultOpen,
  open: openProp,
  onOpenChange,
  activeCount,
  onClear,
  className,
  children,
  ...rest
}: FiltersSectionProps) {
  const isControlled = openProp !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  );
  const open = isControlled ? openProp : uncontrolledOpen;
  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const hasActive = activeCount != null && activeCount > 0;

  return (
    <CollapsiblePrimitive.Root
      open={open}
      onOpenChange={handleOpenChange}
      render={
        <div
          data-slot="filters-section"
          {...rest}
          className={cn(
            "max-w-full overflow-hidden border-b border-contrast/5 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
            className,
          )}
        />
      }
    >
      <CollapsiblePrimitive.Trigger
        render={
          <button
            type="button"
            className="flex w-full min-w-0 max-w-full cursor-pointer items-center gap-2 overflow-hidden p-3"
          >
            <div
              className={cn(
                "size-4 shrink-0 transition-opacity",
                !(open || hasActive) && "opacity-50",
              )}
            >
              <Icon
                name={icon}
                size={16}
                className={
                  open
                    ? "text-[#1a3fcc] dark:text-[#b2c5ff]"
                    : hasActive
                      ? "text-[#ffaf53]"
                      : "text-heading"
                }
              />
            </div>
            <span
              className={cn(
                "flex-1 text-left text-sm font-medium leading-normal transition-colors",
                open
                  ? "text-[#1a3fcc] dark:text-[#b2c5ff]"
                  : hasActive
                    ? "text-[#ffaf53]"
                    : "text-contrast/90",
              )}
            >
              {label}
            </span>
            {hasActive && onClear ? (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onClear();
                  }
                }}
                className="shrink-0 cursor-pointer text-xs font-normal text-contrast/60 transition-colors hover:text-contrast/80"
              >
                Clear
              </span>
            ) : null}
            {hasActive ? (
              <div
                className={cn(
                  "flex h-5 min-w-5 items-center justify-center rounded border-[0.714px] px-1 shadow-[0px_0.714px_1.429px_0px_rgba(0,0,0,0.05)]",
                  open
                    ? "border-contrast/15 bg-contrast/4"
                    : "border-[#ffaf53]/25 bg-[#ffaf53]/15",
                )}
              >
                <span
                  className={cn(
                    "text-[11px] font-medium leading-[14.286px] tabular-nums",
                    open ? "text-contrast/65" : "text-[#ffaf53]",
                  )}
                >
                  {activeCount}
                </span>
              </div>
            ) : null}
            <motion.div
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.15 }}
              className="shrink-0 opacity-40"
            >
              <Icon
                name="IconChevronDownSmall"
                size={16}
                className="text-heading"
              />
            </motion.div>
          </button>
        }
      />
      <CollapsiblePrimitive.Panel
        keepMounted
        render={(panelProps, state) => (
          <SectionMotionPanel panelProps={panelProps} state={state} />
        )}
      >
        {children}
      </CollapsiblePrimitive.Panel>
    </CollapsiblePrimitive.Root>
  );
}

export type FiltersSectionContentProps = {
  gap?: "tight" | "default";
} & React.ComponentProps<"div">;

function SectionContent({
  className,
  gap = "default",
  children,
  ...rest
}: FiltersSectionContentProps) {
  return (
    <div
      data-slot="filters-section-content"
      className={cn(
        "flex max-w-full min-w-0 flex-col overflow-hidden px-3 pb-3",
        gap === "tight" ? "gap-1.5" : "gap-3",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export type FilterLabelProps = React.ComponentProps<typeof Label>;

function FilterLabel(props: FilterLabelProps) {
  return <Label data-slot="filter-label" {...props} />;
}

export type ClearChipsButtonProps = React.ComponentProps<"button">;

function ClearChipsButton({
  className,
  children,
  type = "button",
  ...rest
}: ClearChipsButtonProps) {
  return (
    <button
      data-slot="filter-clear-chips"
      type={type}
      className={cn(
        "flex h-[26px] cursor-pointer items-center px-1.5 text-[11px] font-medium text-contrast/40 transition-colors hover:text-contrast/70",
        className,
      )}
      {...rest}
    >
      {children ?? "Clear"}
    </button>
  );
}

export type FilterFieldContainerProps = React.ComponentProps<"div">;

function FilterFieldContainer({
  className,
  children,
  ...rest
}: FilterFieldContainerProps) {
  return (
    <div
      data-slot="filter-field-container"
      className={cn("flex w-full flex-col gap-1", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export type FilterFieldLabelProps = FilterLabelProps;

function FilterFieldLabel(props: FilterFieldLabelProps) {
  return <FilterLabel data-slot="filter-field-label" {...props} />;
}

export type FilterFieldContentProps = React.ComponentProps<"div">;

function FilterFieldContent({
  className,
  children,
  ...rest
}: FilterFieldContentProps) {
  return (
    <div data-slot="filter-field-content" className={cn(className)} {...rest}>
      {children}
    </div>
  );
}

export type FilterTextInputProps = {
  value: string;
  onChange: (next: string) => void;
  /**
   * Shows an ⏎ keycap at the right edge while there is a draft to commit. Set it
   * on fields that hold *several* values, where typing alone changes nothing
   * until the draft becomes a chip — the room for the keycap is reserved up
   * front so the text never shifts as it appears.
   */
  enterHint?: boolean;
} & Omit<React.ComponentProps<typeof Input>, "onChange" | "variant" | "size">;

/** The panel's text field — one shell for every free-text filter input. */
function FilterTextInput({
  value,
  onChange,
  type = "text",
  className,
  enterHint = false,
  ...rest
}: FilterTextInputProps) {
  const input = (
    <Input
      type={type}
      variant="filter"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(enterHint && "pr-9", className)}
      aria-keyshortcuts={enterHint ? "Enter" : undefined}
      {...rest}
    />
  );

  if (!enterHint) return input;

  return (
    <div className="relative flex w-full items-center">
      {input}
      {value.trim().length > 0 && (
        <span
          aria-hidden
          data-slot="filter-enter-hint"
          className="pointer-events-none absolute inset-y-0 right-2 flex items-center"
        >
          <KbdShortcut keys={["↵"]} />
        </span>
      )}
    </div>
  );
}

export type FilterChipActionsProps = {
  excluded: boolean;
  onToggleExclude?: () => void;
  onRemove: () => void;
  /** Omit both to drop the required/optional asterisk entirely. */
  required?: boolean;
  onToggleRequired?: () => void;
  className?: string;
};

function FilterChipActions({
  excluded,
  onToggleExclude,
  onRemove,
  required = false,
  onToggleRequired,
  className,
}: FilterChipActionsProps) {
  const requiredLabel = required
    ? "Required (every required entity must match)"
    : "Optional (any optional entity may match)";
  const excludeLabel = excluded ? "Un-exclude" : "Exclude";

  return (
    <div className={cn("flex shrink-0 items-center gap-1", className)}>
      {onToggleRequired && !excluded && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              tabIndex={0}
              onClick={onToggleRequired}
              aria-pressed={required}
              aria-label={requiredLabel}
              className="shrink-0 cursor-pointer transition-opacity"
            >
              <Icon
                name="IconAsterisk"
                size={12}
                className={
                  required
                    ? "text-ai"
                    : "text-contrast opacity-30 hover:opacity-60"
                }
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" align="center">
            {requiredLabel}
          </TooltipContent>
        </Tooltip>
      )}
      {onToggleExclude && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              tabIndex={0}
              onClick={onToggleExclude}
              aria-label={excludeLabel}
              className="shrink-0 cursor-pointer transition-opacity"
            >
              <Icon
                name="IconCircleBanSign"
                size={12}
                className={
                  excluded
                    ? "text-destructive dark:text-red-400"
                    : "text-contrast opacity-30 hover:opacity-60"
                }
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" align="center">
            {excludeLabel}
          </TooltipContent>
        </Tooltip>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            tabIndex={0}
            onClick={onRemove}
            aria-label="Remove"
            className="shrink-0 cursor-pointer opacity-40 transition-opacity hover:opacity-70"
          >
            <Icon name="IconCrossMedium" size={12} className="text-heading" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="center">
          Remove
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

/** The muted `+N` after a chip label when the chip also matches N alias forms. */
function FilterVariantCount({ count }: { count: number }) {
  if (count === 0) return null;
  return <span className="ml-1 text-contrast/40">+{count}</span>;
}

export type FilterChipProps = {
  label: string;
  excluded?: boolean;
  onRemove: () => void;
  /** Omit to render a chip that can only be removed. */
  onToggleExclude?: () => void;
  required?: boolean;
  /** Omit to drop the required/optional asterisk (panels without that concept). */
  onToggleRequired?: () => void;
  iconBefore?: React.ReactNode;
  /** Alias forms of `label`; shown as a muted `+N` with every form in the title. */
  variants?: string[] | null;
  /** Opt-in control rendered just before the action row (e.g. a variants editor). */
  beforeActions?: React.ReactNode;
};

/**
 * An applied filter value. Excluded chips read as struck-through and tinted
 * destructive so a negated term is never mistaken for an included one.
 */
function FilterChip({
  label,
  excluded = false,
  onRemove,
  onToggleExclude,
  required,
  onToggleRequired,
  iconBefore,
  variants,
  beforeActions,
}: FilterChipProps) {
  const variantCount = variants?.length ?? 0;
  const formsTitle = variantCount > 0 ? formatEntityForms({ text: label, variants }) : undefined;
  return (
    <div
      data-slot="filter-chip"
      className={cn(
        "flex h-[26px] max-w-full min-w-0 items-center gap-1 overflow-hidden rounded border px-1.5",
        excluded
          ? "border-destructive/30 bg-destructive/6"
          : "border-border bg-contrast/4",
      )}
    >
      {iconBefore}
      <span
        title={formsTitle}
        className={cn(
          "min-w-0 flex-1 truncate text-xs font-normal tracking-[-0.12px]",
          excluded
            ? "text-destructive/80 line-through dark:text-red-400/80"
            : "text-contrast/80",
        )}
      >
        {label}
        <FilterVariantCount count={variantCount} />
      </span>
      {beforeActions}
      <FilterChipActions
        excluded={excluded}
        onToggleExclude={onToggleExclude}
        onRemove={onRemove}
        required={required}
        onToggleRequired={onToggleRequired}
      />
    </div>
  );
}

export type FilterTagProps = {
  label: string;
  onRemove: () => void;
} & React.ComponentProps<"div">;

function FilterTag({ label, onRemove, className, ...rest }: FilterTagProps) {
  return (
    <div
      data-slot="filter-tag"
      className={cn(
        "flex items-center overflow-hidden rounded-md border border-contrast/5",
        className,
      )}
      {...rest}
    >
      <div className="flex max-h-[45px] flex-1 items-center gap-1.5 bg-contrast/4 p-2">
        <span className="flex-1 truncate text-[13px] font-normal tracking-[-0.13px] text-contrast/80">
          {label}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 cursor-pointer opacity-50 transition-opacity hover:opacity-80"
        >
          <Icon name="IconCrossMedium" size={12} className="text-heading" />
        </button>
      </div>
    </div>
  );
}

export const Filters = {
  Container,
  Header,
  SubHeader,
  Body,
  Footer,
  Section,
  SectionContent,
  PoolTabs,
  Label: FilterLabel,
  ClearChipsButton,
  FieldContainer: FilterFieldContainer,
  FieldLabel: FilterFieldLabel,
  FieldContent: FilterFieldContent,
  TextInput: FilterTextInput,
  Chip: FilterChip,
  VariantCount: FilterVariantCount,
  ChipActions: FilterChipActions,
  Tag: FilterTag,
};

export {
  Container as FiltersContainer,
  Header as FiltersHeader,
  SubHeader as FiltersSubHeader,
  Body as FiltersBody,
  Footer as FiltersFooter,
  Section as FiltersSection,
  SectionContent as FiltersSectionContent,
  PoolTabs as FiltersPoolTabs,
  FilterLabel,
  ClearChipsButton,
  FilterFieldContainer,
  FilterFieldLabel,
  FilterFieldContent,
  FilterTextInput,
  FilterChip,
  FilterVariantCount,
  FilterChipActions,
  FilterTag,
};
